import PropTypes from 'prop-types';
import { useCallback } from 'react';
import cx from 'classnames';
import { format } from 'date-fns/format';
import { Drawer, DrawerBody } from '@cat-ecom/pcc-components';
import { CatIconCalendar } from '@blocks-react/components/Icons/Calendar';
import { CatCalendar } from '@blocks-react/components/Calendar';
import { Input } from 'cat-ecommerce-alloy';
import Field from '@app/components/common/form/Field';
import CustomHeaderDrawer from '../CustomDrawer/CustomHeaderDrawer/CustomHeaderDrawer';
import styles from './DrawerCalendar.module.scss';
import { DRAWER_DROPDOWN_VERTICAL_MAX_HEIGHT } from '../DrawerDropdown';

const DrawerCalendar = ({
  closeOnSelect = true,
  drawerConfiguration,
  drawerTitle,
  label,
  name,
  onChange,
  placeholder,
  selectedValue,
  shouldRenderCalendarField = false,
  showRequired = false,
  validator,
  activeDate,
  activeDateFormat
}) => {
  const BLOCKS_MIN_DATE_FORMAT = 'yyyy/MM/dd';
  const handleSelect = useCallback(
    value => {
      onChange(value);
      if (closeOnSelect) {
        drawerConfiguration.closeDrawer();
      }
    },
    [closeOnSelect, drawerConfiguration, onChange]
  );

  return (
    <div className={cx({ 'w-100': shouldRenderCalendarField })}>
      {shouldRenderCalendarField && (
        <>
          <label
            htmlFor={name}
            className={cx(
              styles['drawer-calendar__label'],
              'font-regular',
              'text-univers--bold'
            )}
          >
            {label}
            {showRequired && '*'}
          </label>
          <button
            className={cx(
              'w-100 p-0 mb-3 border-0 position-relative',
              styles['drawer-calendar__open-btn']
            )}
            name={name}
            onClick={e => {
              e.preventDefault();
              drawerConfiguration.openDrawer();
            }}
          >
            <div
              className={cx(styles.cover, 'w-100 h-100 position-absolute')}
            />
            <div
              className={cx(
                styles['input-container'],
                'flex items-center justify-center'
              )}
            >
              <Field
                type="text"
                as={Input}
                value={selectedValue}
                name={name}
                id={name}
                placeholder={placeholder}
                showRequired={showRequired}
                validator={validator}
              />
              <CatIconCalendar
                className={cx(styles.icon, 'position-absolute')}
              />
            </div>
          </button>
        </>
      )}
      <Drawer
        {...drawerConfiguration.drawerProps}
        isActive={drawerConfiguration.isDrawerOpen}
        orientation="bottom"
        onBlDrawerClose={drawerConfiguration.closeDrawer}
        verticalMaxHeight={DRAWER_DROPDOWN_VERTICAL_MAX_HEIGHT.LARGE}
        hideCloseButton
      >
        <CustomHeaderDrawer
          title={drawerTitle}
          onCloseDrawer={drawerConfiguration.closeDrawer}
          showDivider
        />
        <DrawerBody className="pt-3 pb-5">
          <CatCalendar
            dateFormat={activeDateFormat}
            setActiveDate={activeDate}
            disabledMinDate={format(new Date(), BLOCKS_MIN_DATE_FORMAT)}
            onBlCalendarChange={handleSelect}
          />
        </DrawerBody>
      </Drawer>
    </div>
  );
};

DrawerCalendar.propTypes = {
  closeOnSelect: PropTypes.bool,
  drawerTitle: PropTypes.string,
  drawerConfiguration: PropTypes.shape({
    drawerProps: PropTypes.shape({}),
    isDrawerOpen: PropTypes.bool,
    closeDrawer: PropTypes.func,
    openDrawer: PropTypes.func
  }),
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  selectedValue: PropTypes.string,
  shouldRenderCalendarField: PropTypes.bool,
  showRequired: PropTypes.bool,
  validator: PropTypes.func,
  selectedDate: PropTypes.string,
  activeDate: PropTypes.string,
  activeDateFormat: PropTypes.string
};

export default DrawerCalendar;
